import { FC, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import useNoodleApi from '@/hooks/useNoodleApi';
import getMemberDetails from '@/tsClient/getMemberDetails';
import sendEmailByTemplate from '@/tsClient/sendEmailByTemplate';
import Buttons from '@/components/Buttons';
import Copy from '@/components/Icons/Copy';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import { useToast } from '@/hooks';
import { m } from 'framer-motion';
import Button from '@/components/DesignLibrary/Button';
import usePopover from '@/hooks/usePopover';
import ProgressIndicator from '@/components/ProgressIndicator';
import WarningCircle from '@/components/Icons/WarningCircle';
import Tooltip from '@/components/Tooltip';

import s from './UserWorkflowPerson.module.scss';

const NO_ACCOUNT_YET = 'Not accepted invite';

/**
 * Component that displays a warning and button to resend account initiation email
 * when a user doesn't have an account yet
 */
const NoAccountYet: FC<{ creatorId: string; personId: string; userWorkflowId: string }> = ({ creatorId, personId, userWorkflowId }) => {
  const { fetchingState: resendInitiationEmailState, getData: resendInitiationEmailFn } = useNoodleApi(sendEmailByTemplate, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.INFO, 'Case initiation email sent'],
  });

  const handleResendEmail = (): void => {
    resendInitiationEmailFn({
      creatorId,
      personId,
      template: 'user-workflow-initiated-to-customer',
      userWorkflowId,
    });
  };

  return (
    <div className={s.noAccountYet}>
      <div>
        <WarningCircle weight='fill' color="var(--color-warning)" size={16} />
        {NO_ACCOUNT_YET}
      </div>
      <Button
        variant='secondary'
        size='xs'
        onClick={handleResendEmail}
        loading={resendInitiationEmailState.isFetching}
      >
        Resend invite
      </Button>
    </div>
  );
};

const UserWorkflowPerson: FC<{
  personId: string;
  personName: string;
  compact?: boolean;
  creatorSlug?: string;
  userWorkflowId: string | null;
  creatorId?: string;
  isVerified?: boolean;
}> = ({ personId, personName, compact, creatorSlug, userWorkflowId, creatorId, isVerified }) => {
  const { isOpen, setIsOpen, triggerRef, menuRef } = usePopover('bottom-left');
  const { data: fetchedMemberData, getData: getMemberDetailsFn } = useNoodleApi(getMemberDetails);

  const addToast = useToast();
  const containerRef = useRef<HTMLDivElement>(null);

  async function copyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      addToast(useToast.ToastTypeVariants.SUCCESS, `Copied to clipboard!`);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }

  useEffect(() => {
    if (creatorSlug) {
      getMemberDetailsFn({ creatorSlug, personId });
    }
  }, [personId, creatorSlug, getMemberDetailsFn]);

  return (
    <span className={s.person} ref={containerRef}>
      <Buttons
        ref={triggerRef}
        isWrapper
        onClick={() => setIsOpen(!isOpen)}
        className={compact ? 'body-sm-bold' : 'heading-sm'}
        title={`${isOpen ? 'Hide' : 'Show'} contact's details`}
      >
        {personName} {!isVerified && creatorId && userWorkflowId && (
          <Tooltip text={NO_ACCOUNT_YET} classNameWrapper={s.warning}>
            <WarningCircle weight='fill' color="var(--color-warning)" />
          </Tooltip>
        )}
      </Buttons>
      {isOpen && createPortal(
        <m.ul
          ref={menuRef as unknown as React.RefObject<HTMLUListElement>}
          initial={{
            opacity: 0,
            y: 8,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          className={s.details}
        >
          {!isVerified && creatorId && userWorkflowId && (
            <li>
              <NoAccountYet creatorId={creatorId} personId={personId} userWorkflowId={userWorkflowId} />
            </li>
          )}
          <li>
            <Buttons isWrapper onClick={() => copyToClipboard(personName)} className={s.row}>
              <div>
                <p>Name</p>
                <p>{personName}</p>
              </div>
              <Copy />
            </Buttons>
          </li>
          {creatorSlug && <>
            {fetchedMemberData?.person
              ? (
                <>
                  {fetchedMemberData.person.email && (
                    <li>
                      <Buttons isWrapper onClick={() => copyToClipboard(fetchedMemberData.person?.email || '')} className={s.row}>
                        <div>
                          <p>Email</p>
                          <p>{fetchedMemberData.person.email}</p>
                        </div>
                        <Copy />
                      </Buttons>
                    </li>
                  )}
                  {fetchedMemberData.person.phoneNumber && (
                    <li>
                      <Buttons isWrapper onClick={() => copyToClipboard(fetchedMemberData.person?.phoneNumber || '')} className={s.row}>
                        <div>
                          <p>Login Phone #</p>
                          <p>{fetchedMemberData.person.phoneNumber}</p>
                        </div>
                        <Copy />
                      </Buttons>
                    </li>
                  )}
                  {fetchedMemberData.person.contactPhoneNumber && (
                    <li>
                      <Buttons isWrapper onClick={() => copyToClipboard(fetchedMemberData.person?.contactPhoneNumber || '')} className={s.row}>
                        <div>
                          <p>Contact Phone #</p>
                          <p>{fetchedMemberData.person.contactPhoneNumber}</p>
                        </div>
                        <Copy />
                      </Buttons>
                    </li>
                  )}
                  <li>
                    {fetchedMemberData.conversation?.sid && (
                      <Button
                        variant="secondary"
                        size="sm"
                        href={getUrl(IDENTIFIERS.DASHBOARD_CONVERSATION, {
                          conversationSid: fetchedMemberData.conversation.sid,
                        })}
                        newTab
                        fullWidth
                      >
                        Open Conversation
                      </Button>
                    )}
                    <Button
                      variant="primary"
                      size="sm"
                      href={getUrl(IDENTIFIERS.DASHBOARD_MEMBER, {
                        id: personId,
                      })}
                      newTab
                      fullWidth
                    >
                      Open Profile
                    </Button>
                  </li>
                </>
              )
              : (
                <li>
                  <div className={s.row}>
                    <ProgressIndicator size={16} /> <p className={s.loading}>Loading member details...</p>
                  </div>
                </li>
              )}
          </>}
        </m.ul>,
        document.body,
      )}
    </span>
  );
};

export default UserWorkflowPerson;
